import type { DashboardRange } from "@/lib/dashboard-analytics";

export type DisbursementMode = "amount" | "count";

export interface DisbursementSeriesPoint {
  month: string;
  newCustomer: number;
  repeatCustomer: number;
}

export interface DisbursementDetailRow {
  id: string;
  date: string;
  customerName: string;
  amount: number;
  status: "New" | "Repeat";
}

export interface AdminDashboardDisbursementTrend {
  range: DashboardRange;
  mode: DisbursementMode;
  series: DisbursementSeriesPoint[];
  details: DisbursementDetailRow[];
}

export const EMPTY_DISBURSEMENT_TREND: AdminDashboardDisbursementTrend = {
  range: "this_month",
  mode: "amount",
  series: [],
  details: [],
};

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Normalize GET /rental-schedule/admin-dashboard-disbursement-trend */
export function normalizeAdminDashboardDisbursementTrend(
  payload: unknown,
  fallbackRange: DashboardRange,
  fallbackMode: DisbursementMode
): AdminDashboardDisbursementTrend {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;

  if (!root || typeof root !== "object") {
    return { ...EMPTY_DISBURSEMENT_TREND, range: fallbackRange, mode: fallbackMode };
  }

  const body = root as Record<string, unknown>;
  const seriesRaw = Array.isArray(body.series) ? body.series : [];
  const detailsRaw = Array.isArray(body.details) ? body.details : [];

  return {
    range: (typeof body.range === "string" ? body.range : fallbackRange) as DashboardRange,
    mode: body.mode === "count" ? "count" : "amount",
    series: seriesRaw.map((row) => {
      const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
      return {
        month: String(r.month ?? ""),
        newCustomer: Math.round(num(r.newCustomer ?? r.new_customer)),
        repeatCustomer: Math.round(num(r.repeatCustomer ?? r.repeat_customer)),
      };
    }),
    details: detailsRaw.map((row) => {
      const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
      return {
        id: String(r.id ?? ""),
        date: String(r.date ?? ""),
        customerName: String(r.customerName ?? r.customer_name ?? ""),
        amount: Math.round(num(r.amount)),
        status: String(r.status ?? "New") === "Repeat" ? "Repeat" : "New",
      };
    }),
  };
}
